import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Target, Users, Zap, Award } from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";

const AboutPage = () => {
  const values = [
    { icon: Target, title: "Healthcare Focused", description: "We build exclusively for healthcare. Every feature is designed with clinical workflows in mind." },
    { icon: Users, title: "Customer Success", description: "Your success is our success. Dedicated support and training to help you get the most from our platform." },
    { icon: Zap, title: "Innovation", description: "We continuously improve our platform based on customer feedback and industry best practices." },
    { icon: Award, title: "Security First", description: "HIPAA compliance and data security are built into everything we do, not bolted on." }
  ];

  const stats = [
    { number: "500+", label: "Healthcare Clients" },
    { number: "99.9%", label: "Uptime SLA" },
    { number: "2M+", label: "Patients Managed" },
    { number: "24/7", label: "Support" }
  ];

  return (
    <div data-testid="about-page">
      <PageHeader 
        title="About NeoBody"
        subtitle="Healthcare software built by people who understand healthcare."
        breadcrumbs={[{ name: "About" }]}
      />

      {/* Mission Section */}
      <section className="py-20 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="gold-divider" />
                <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">Our Mission</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] mb-6">
                Empowering
                <br />
                <span className="italic text-[#1B4D3E]">Healthcare</span>
              </h2>
              <p className="text-lg text-[#4A4A4A] font-light leading-relaxed mb-6">
                We believe healthcare providers should focus on what they do best—caring for patients. 
                That's why we built NeoBody: to handle the administrative complexity so you don't have to.
              </p>
              <p className="text-lg text-[#4A4A4A] font-light leading-relaxed mb-6">
                Founded by healthcare professionals and technologists, we understand the unique challenges 
                of running a healthcare organization. Our platform is built from the ground up to address 
                these challenges with intuitive, powerful tools.
              </p>
              <p className="text-lg text-[#4A4A4A] font-light leading-relaxed">
                From solo practitioners to large health systems, we partner with organizations across the 
                healthcare spectrum to streamline operations, improve patient experiences, and drive growth.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800"
                alt="NeoBody Office"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    {[1,2,3].map(i => (
                      <div key={i} className="w-10 h-10 rounded-full bg-[#1B4D3E] border-2 border-white" />
                    ))}
                  </div>
                  <div>
                    <div className="flex text-[#D4AF37] mb-1">
                      {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-current" />)}
                    </div>
                    <p className="text-sm text-[#4A4A4A]">4.9 on G2 Crowd</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-[#1B4D3E]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-display text-4xl md:text-5xl text-white mb-2">{stat.number}</div>
                <div className="text-white/60 text-sm tracking-[0.1em] uppercase">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="gold-divider" />
              <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">Our Values</span>
              <div className="gold-divider" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A]">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-[#F9F9F7] rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-[#1B4D3E]/10 flex items-center justify-center mb-6">
                  <value.icon className="w-6 h-6 text-[#1B4D3E]" />
                </div>
                <h3 className="font-display text-xl text-[#1A1A1A] mb-3">{value.title}</h3>
                <p className="text-[#4A4A4A] font-light">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NeoBody */}
      <section className="py-20 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="gold-divider" />
                <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">Why NeoBody</span>
              </div>
              <h2 className="font-display text-4xl text-[#1A1A1A] mb-6">
                Built Different
              </h2>
              <p className="text-lg text-[#4A4A4A] font-light leading-relaxed mb-6">
                Unlike generic business software adapted for healthcare, NeoBody was designed from day one 
                specifically for healthcare organizations. Every feature, workflow, and integration reflects 
                years of experience working with healthcare providers.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                  <span className="text-[#4A4A4A]">Purpose-built for healthcare workflows</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                  <span className="text-[#4A4A4A]">HIPAA compliance built-in, not bolted-on</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                  <span className="text-[#4A4A4A]">Integrates with your existing EHR and tools</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                  <span className="text-[#4A4A4A]">Dedicated healthcare support team</span>
                </div>
              </div>
              <Link to="/book" className="btn-primary">
                See a Demo
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800"
                alt="Healthcare Professional"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] mb-6">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-lg text-[#4A4A4A] font-light max-w-2xl mx-auto mb-10">
            Join 500+ healthcare organizations already using NeoBody to streamline their operations.
          </p>
          <Link to="/book" className="btn-gold">
            Request Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
